/**
 * Push Notifications Hook
 * 
 * Handles Web Push subscription lifecycle:
 * - Check if push is supported & permission status
 * - Request permission & subscribe to push
 * - Send subscription to server
 * - Unsubscribe
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';

const API_BASE = '/api/stats';

/**
 * Convert a base64 VAPID key to Uint8Array for PushManager.subscribe()
 */
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

export function usePushNotifications() {
    const { token } = useAuthStore();
    const [permission, setPermission] = useState(
        typeof Notification !== 'undefined' ? Notification.permission : 'denied'
    );
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Check if push is supported
    const isSupported = typeof window !== 'undefined'
        && 'serviceWorker' in navigator
        && 'PushManager' in window
        && 'Notification' in window;

    // Check existing subscription on mount and after token changes
    useEffect(() => {
        if (!isSupported || !token) return;

        let cancelled = false;
        (async () => {
            try {
                const registration = await navigator.serviceWorker.ready;
                const existing = await registration.pushManager.getSubscription();
                if (!cancelled) {
                    setIsSubscribed(!!existing);
                    setPermission(Notification.permission);
                }
            } catch {
                // Non-critical
            }
        })();
        return () => { cancelled = true; };
    }, [isSupported, token]);

    /**
     * Subscribe to push notifications.
     * 1. Request notification permission
     * 2. Get VAPID key from server
     * 3. Subscribe via PushManager
     * 4. Send subscription to server
     */
    const subscribe = useCallback(async () => {
        if (!isSupported || !token) return false;
        setIsLoading(true);

        try {
            // 1. Check if we already have permission, to completely bypass the Edge hang if they manually allowed it
            let perm = Notification.permission;
            console.log('[PushNotifications] Current permission state:', perm);
            
            if (perm !== 'granted') {
                console.log('[PushNotifications] Requesting permission...');
                perm = await Promise.race([
                    Notification.requestPermission(),
                    new Promise((resolve) => setTimeout(() => {
                        console.warn('[PushNotifications] Permission request timed out after 5s');
                        resolve('timeout');
                    }, 5000))
                ]);
                
                console.log('[PushNotifications] New permission result:', perm);
                
                if (perm === 'timeout') {
                    throw new Error('Browser is blocking the permission prompt. Please allow notifications manually from the address bar lock icon.');
                }
            }
            
            setPermission(perm);
            if (perm !== 'granted') {
                setIsLoading(false);
                return false;
            }

            // 2. Get VAPID public key
            console.log('[PushNotifications] Fetching VAPID key...');
            const vapidRes = await fetch(`${API_BASE}/push/vapid-key`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!vapidRes.ok) throw new Error('Push not configured on server');
            const { publicKey } = await vapidRes.json();
            console.log('[PushNotifications] Got VAPID key');

            // 3. Wait for SW to be ready (with timeout to avoid hanging)
            console.log('[PushNotifications] Waiting for service worker...');
            const registration = await Promise.race([
                navigator.serviceWorker.ready,
                new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Service worker not ready after 10s — try reloading')), 10000)
                )
            ]);
            console.log('[PushNotifications] SW ready, subscribing to push...');

            // 4. Subscribe via PushManager
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey)
            });
            console.log('[PushNotifications] Push subscription created');

            // 5. Send subscription to server
            const subRes = await fetch(`${API_BASE}/push/subscribe`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ subscription: subscription.toJSON() })
            });

            if (!subRes.ok) throw new Error('Failed to save subscription');
            console.log('[PushNotifications] Subscription saved to server');

            setIsSubscribed(true);
            return true;
        } catch (err) {
            console.error('[PushNotifications] Subscribe failed:', err);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [isSupported, token]);

    /**
     * Unsubscribe from push notifications.
     */
    const unsubscribe = useCallback(async () => {
        if (!isSupported || !token) return;
        setIsLoading(true);

        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                // Remove from server
                await fetch(`${API_BASE}/push/unsubscribe`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ endpoint: subscription.endpoint })
                });

                // Unsubscribe locally
                await subscription.unsubscribe();
            }
            setIsSubscribed(false);
        } catch (err) {
            console.error('[PushNotifications] Unsubscribe failed:', err);
        } finally {
            setIsLoading(false);
        }
    }, [isSupported, token]);

    return {
        isSupported,
        permission,
        isSubscribed,
        isLoading,
        subscribe,
        unsubscribe
    };
}
